/**
 * <sticky-atc> — appears when the main buybox ATC button scrolls out of view.
 *
 * - Observes the element matching `data-target` (default `[data-add-button]`).
 * - Mirrors variant changes from the main <medcline-product-form> so price and
 *   availability stay in sync (listens to the variant input + cart events).
 * - Clicking the sticky Add button programmatically clicks the main ATC so all
 *   form data (selected options, qty) goes through the same submit pipeline
 *   that <medcline-cart-drawer> already intercepts.
 */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    const selector = this.dataset.target || '[data-add-button]';
    this.target = document.querySelector(selector);
    if (!this.target) return;

    this.button = this.querySelector('[data-sticky-add]');
    this.textEl = this.querySelector('[data-sticky-add-text]');
    this.priceEl = this.querySelector('[data-sticky-price]');

    // Observe the main ATC; show this bar when it's out of view
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const shouldShow = !entry.isIntersecting;
        this.setAttribute('data-shown', shouldShow ? 'true' : 'false');
        this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      },
      { rootMargin: '0px 0px -20% 0px', threshold: 0 }
    );
    this.observer.observe(this.target);

    // Forward sticky click to the real ATC so all submit logic runs once
    if (this.button) {
      this.button.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.target.disabled) return;
        this.target.click();
      });
    }

    // Mirror the real ATC's enabled/disabled + label state
    this._syncFromTarget();
    this._mo = new MutationObserver(() => this._syncFromTarget());
    this._mo.observe(this.target, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true });
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this._mo) this._mo.disconnect();
  }

  _syncFromTarget() {
    if (!this.button || !this.target) return;
    this.button.disabled = !!this.target.disabled;
    const targetText = this.target.querySelector('[data-add-text]');
    if (targetText && this.textEl) {
      this.textEl.textContent = targetText.textContent.trim();
    }
    // Mirror price if main price block updates
    const mainCurrent = document.querySelector('.product-buybox__price [data-price-current]');
    const stickyCurrent = this.priceEl && this.priceEl.querySelector('[data-price-current]');
    if (mainCurrent && stickyCurrent && mainCurrent.textContent !== stickyCurrent.textContent) {
      stickyCurrent.textContent = mainCurrent.textContent;
    }
  }
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyAtc);
}
